import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Card, Row, Col, Form } from 'react-bootstrap';
import moment from 'moment';
import MeterChart from './MeterChart';
import { useGraphData } from '../../hooks/useMeters';
import { buildWidgetConfig } from '../../services/meterApi';
import { getAggregationUnit } from '../../utils/meterGraphs';
import { gapReasonFor } from '../../utils/meterFilters';
import { relativeRangeWithin, TIME_RANGES } from '../../utils/financialYearWindow';
import { useInsights } from '../../../../../globalComponents/insights/useInsights';
import InsightsPanel from '../../../../../globalComponents/insights/InsightsPanel';

/**
 * Namespace the insights cache is scoped under.
 *
 * Every dashboard in the product shares one module-level cache, keyed
 * `tab::graphId`. Each card here keys itself by meter as well as by chart,
 * because three meters all have a chart called netConsumption and the second
 * meter opened would otherwise be shown the first one's narrative.
 */
const INSIGHTS_TAB = 'meter-details';

/**
 * One chart on the meter details page, with its own time range and aggregation.
 * @param {string} currentFY - Financial year the series is scoped to.
 * @param {string} title - Chart heading.
 * @param {string} description - Supporting line under the heading.
 * @param {object} meter - Meter as returned by the meters API.
 * @param {object} currentFYKpi - The meter's KPI mapping for that year.
 * @param {string} graphType - instantaneousFlow, massTotalizer or netConsumption.
 * @param {boolean} autoRefresh - Whether to poll for fresh data every minute.
 * @param {string} aggregationPeriod - Initial aggregation.
 * @param {string} timeRangePeriod - Initial time range.
 * @param {boolean} showInsights - Whether the page's insights switch is on.
 * @param {Function} onInsightsReady - Told what this chart's narrative says, so
 *   the page can summarise the meter's three views at once.
 */
const MeterGraphCard = ({
  currentFY,
  title,
  description,
  meter,
  currentFYKpi,
  graphType,
  autoRefresh,
  aggregationPeriod = 'hour',
  timeRangePeriod = 'day',
  showInsights = false,
  onInsightsReady
}) => {
  // Individual state for each graph
  const [timeRange, setTimeRange] = useState(timeRangePeriod);  // ✅ Use prop
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [aggregation, setAggregation] = useState(aggregationPeriod);  // ✅ Use prop

  // Track what was changed last to prevent infinite loops
  const lastChanged = useRef(null);  // ✅ Changed from 'timeRange' to null
  const isInitialMount = useRef(true);  // ✅ Track initial mount

  // Bidirectional sync between time range and aggregation
  useEffect(() => {
    // Skip on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (lastChanged.current === 'timeRange') {
      // Time range changed → update aggregation
      const defaultAggregations = {
        'hour': 'minute',
        'day': 'hour',
        'week': 'day',
        'month': 'day',
        'thisMonth': 'day',
        'thisQuarter': 'day',
        'thisFY': 'month',
        'custom': 'hour'
      };
      setAggregation(defaultAggregations[timeRange]);
    }
  }, [timeRange]);

  useEffect(() => {
    // Skip on initial mount
    if (isInitialMount.current) {
      return;
    }

    if (lastChanged.current === 'aggregation') {
      // Aggregation changed → update time range
      const defaultTimeRanges = {
        'minute': 'hour',
        'hour': 'day',
        'day': 'week',
        'month': 'month'
      };
      const newTimeRange = defaultTimeRanges[aggregation];
      if (newTimeRange && timeRange !== 'custom') {
        setTimeRange(newTimeRange);
      }
    }
  }, [aggregation, timeRange]);

  // Handlers that track what was changed
  const handleTimeRangeChange = (e) => {
    lastChanged.current = 'timeRange';
    setTimeRange(e.target.value);
  };

  const handleAggregationChange = (e) => {
    lastChanged.current = 'aggregation';
    setAggregation(e.target.value);
  };

  /**
   * The window the chart asks for.
   *
   * This used to be a switch of its own, and it had drifted from the one the
   * water dashboard uses: subtracting seven days from a mid-afternoon now
   * starts the window inside the eighth day back, and the response buckets by
   * calendar day, so "Last 7 Days" drew eight bars here while the dashboard
   * drew seven. "Last 30 Days" was a calendar month, which is thirty-one days
   * or thirty-two.
   *
   * Resolving both pages through one helper also means a past financial year
   * is read from its own end rather than from today, which is what made
   * choosing an old year on this page match nothing.
   */
  const dateRange = useMemo(
    () =>
      relativeRangeWithin(timeRange, currentFY, {
        start: customStart,
        end: customEnd,
      }),
    [timeRange, customStart, customEnd, currentFY]
  );

  // ✅ Dynamic title with units and aggregation
  const graphUnit = useMemo(() => ({
    instantaneousFlow: 'm³/h',
    massTotalizer: 'm³',
    netConsumption: meter?.unit || '',
  }[graphType] || ''), [graphType, meter?.unit]);

  const subHeading = useMemo(() => {
    const cadence = {
      minute: 'Per minute',
      hour: 'Hourly',
      day: 'Daily',
      month: 'Monthly',
    }[aggregation] || 'Daily';

    return [cadence, graphUnit].filter(Boolean).join(', ') +
      (currentFY ? ` · FY ${currentFY}` : '');
  }, [aggregation, graphUnit, currentFY]);

  const graphConfig = useMemo(() => {
    if (!meter || !currentFYKpi) return null;

    const baseParams = {
      meterId: meter.meterId,
      financialYear: currentFY,
      startDateTime: dateRange.start.toISOString(),
      endDateTime: dateRange.end.toISOString(),
      locationId: meter.sourceId?.toString() || '1',
      module: currentFYKpi.module,
      aggregationPeriodValue: 1,
      aggregationPeriodUnit: getAggregationUnit(aggregation),
      chartType: 'line'
    };

    switch (graphType) {
      case 'instantaneousFlow':
        return buildWidgetConfig({
          ...baseParams,
          category: 'Flow',
          subCategory: 'Rate',
          kpi: 'Volume Flow',
          kpiType: 'INTERNAL',
          measurementType: 'INSTANT',
          title: 'Instantaneous Flow'
        });
      
      case 'massTotalizer':
        return buildWidgetConfig({
          ...baseParams,
          enableStacking: true,
          stackByField: 'kpi',
          category: 'Mass Totalizer',
          kpiType: 'INTERNAL',
          measurementType: 'INSTANT',
          title: 'Mass Totalizer'
        });
      
      case 'netConsumption':
        return buildWidgetConfig({
          ...baseParams,
          category: currentFYKpi.category,
          subCategory: currentFYKpi.subCategory,
          kpi: currentFYKpi.kpi,
          kpiType: 'EXTERNAL',
          title: 'Net Consumption'
        });
      
      default:
        return null;
    }
  }, [meter, currentFYKpi, currentFY, dateRange, aggregation, graphType]);

  // Fetch graph data
  const { data: graphData, isLoading, refetch } = useGraphData(
    graphConfig,
    { enabled: !!graphConfig }
  );

    const transformedGraphData = useMemo(() => {
    const data = graphData?.data || [];
    if (!data.length) return data;

    return data.map((series) => {
      if (series.kpi === 'Reverse Flow') {
        return {
          ...series,
          value: isNaN(Number(series.value)) ? 0 : -Number(series.value),
          sum_value: isNaN(Number(series.value)) ? 0 : -Number(series.value),
        };
      }
      return series;
    });
  }, [graphData]);

  /**
   * The rows the chart draws, not the rows the request returned. Reverse flow
   * is negated above so the two directions read against one axis, and a
   * narrative written from the response would call those volumes positive.
   */
  const insightsData = useMemo(
    () => ({ data: transformedGraphData }),
    [transformedGraphData]
  );

  /**
   * Nothing is asked for until this card has something to be asked about. An
   * in-flight request leaves the series empty, which is indistinguishable from
   * a meter that reported nothing, and a meter with no KPI mapped for the year
   * has no request to make at all.
   */
  const insightsEnabled = showInsights && !!graphConfig && !isLoading;

  const {
    insights,
    loading: insightsLoading,
    error: insightsError,
    refresh: refreshInsights
  } = useInsights(
    INSIGHTS_TAB,
    `${meter?.meterId}-${graphType}`,
    insightsData,
    graphConfig,
    insightsEnabled,
    onInsightsReady
  );

  // Auto-refresh for this graph
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      refetch();
    }, 60000); // 60 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, refetch]);

  return (
    <Card className="graph-card">
      <Card.Header>
        <div className="graph-card-header">
          <div>
            <h5 className="mb-0">{title}</h5>
            <small className="im-graph-sub">{subHeading}</small>
            <small className="im-graph-desc">{description}</small>
          </div>
        </div>
      </Card.Header>
      <Card.Body>
        {/* Graph Controls */}
        <div className="graph-controls">
          <Row className="align-items-end">
            <Col md={3}>
              <Form.Group>
                <Form.Label className="small">Time Range</Form.Label>
                <Form.Select
                  size="sm"
                  value={timeRange}
                  onChange={handleTimeRangeChange} 
                >
                  {TIME_RANGES.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            {timeRange === 'custom' && (
              <>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small">Start Date</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      size="sm"
                      value={customStart ? customStart.format('YYYY-MM-DDTHH:mm') : ''}
                      onChange={(e) => setCustomStart(moment(e.target.value))}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small">End Date</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      size="sm"
                      value={customEnd ? customEnd.format('YYYY-MM-DDTHH:mm') : ''}
                      onChange={(e) => setCustomEnd(moment(e.target.value))}
                    />
                  </Form.Group>
                </Col>
              </>
            )}

            <Col md={3}>
              <Form.Group>
                <Form.Label className="small">Aggregation</Form.Label>
                <Form.Select
                  size="sm"
                  value={aggregation}
                  onChange={handleAggregationChange}
                >
                  <option value="minute">Per Minute</option>
                  <option value="hour">Hourly</option>
                  <option value="day">Daily</option>
                  <option value="month">Monthly</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </div>

        {/* Graph */}
        <div className="graph-container">
          {isLoading ? (
            <div className="im-graph-skeleton" aria-label="Loading chart" />
          ) : !graphConfig ? (
            <div className="text-center p-5 text-muted">
              No KPI is mapped to this meter for {currentFY}, so there is nothing to plot.
            </div>
          ) : (
            <MeterChart
              rows={transformedGraphData}
              graphType={graphType}
              stackByField={graphConfig.stackByField}
              dualAxis={graphType === 'massTotalizer'}
              unit={graphUnit}
              aggregation={aggregation}
              gapReason={gapReasonFor(meter?.readingState)}
              height={350}
            />
          )}
        </div>

        {showInsights && (
          <div className="im-insights-panel">
            <InsightsPanel
              insights={insights}
              loading={insightsLoading}
              error={insightsError}
              onRefresh={refreshInsights}
            />
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default MeterGraphCard;
